/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from "react";
import { LIME, MAX_ZOOM, MIN_ZOOM, resizeFromCorner } from "@/lib/carouselLayout";
import { tr } from "@/lib/i18n";

// Hand sizing: drag corners / drag to move. Shared by the Instagram
// carousel (per-slide photo + panorama), the story cards (Story 9:16 and
// Feed 4:5) and the sponsored Facebook post. Geometry helpers live in
// lib/carouselLayout.js (`resizeFromCorner`, `rectToPlacement`, …).

const CORNERS = ["nw", "ne", "sw", "se"];

/** Natural width ÷ height of one image URL, once loaded (0 until then). */
export function useImageAspect(url) {
  const [aspect, setAspect] = useState({ url: "", value: 0 });
  useEffect(() => {
    if (!url) return undefined;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled && img.naturalWidth && img.naturalHeight) {
        setAspect({ url, value: img.naturalWidth / img.naturalHeight });
      }
    };
    img.src = url;
    return () => {
      cancelled = true;
    };
  }, [url]);
  return aspect.url === url ? aspect.value : 0;
}

/** An editing stage around one or more viewports (a slide, a card, a
 *  post). `rect` is the photo in content px; each viewport is
 *  {key, left, top, width, height, node} in the same content px, and its
 *  `node` is rendered at full size then scaled down. The stage shows
 *  `marginX`/`marginY` content px around the content so the photo can be
 *  seen — and grabbed — where it runs past the edges. Dragging the lime
 *  box moves the photo; dragging a corner resizes it, keeping its shape.
 *  `unitW` is the width `zoom` is measured in (for the slider). */
export default function PhotoStage({
  contentW, contentH, marginX, marginY, scale, rect, src, viewports, unitW,
  minW, maxW, onChange, testid,
}) {
  const drag = useRef(null);
  const frameReq = useRef(0);
  const pending = useRef(null);
  useEffect(() => () => cancelAnimationFrame(frameReq.current), []);
  const push = (next) => {
    pending.current = next;
    if (frameReq.current) return;
    frameReq.current = requestAnimationFrame(() => {
      frameReq.current = 0;
      if (pending.current) onChange(pending.current);
    });
  };
  const down = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { mode, x: e.clientX, y: e.clientY, start: rect };
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.x) / scale;
    const dy = (e.clientY - d.y) / scale;
    push(
      d.mode === "move"
        ? { ...d.start, left: d.start.left + dx, top: d.start.top + dy }
        : resizeFromCorner(d.start, d.mode, dx, dy, { minW, maxW }),
    );
  };
  const up = () => {
    drag.current = null;
  };
  const px = (v, m) => (v + m) * scale;
  const zoom = rect.width / unitW;
  const setZoom = (z) => {
    // Resize about the photo's centre.
    const w = Math.min(maxW, Math.max(minW, z * unitW));
    const h = w * (rect.height / rect.width);
    onChange({
      left: rect.left + rect.width / 2 - w / 2,
      top: rect.top + rect.height / 2 - h / 2,
      width: w,
      height: h,
    });
  };
  return (
    <div className="space-y-2">
      <div
        className="relative overflow-hidden select-none bg-[var(--secondary)] border border-[var(--border)]"
        style={{
          position: "relative", overflow: "hidden",
          width: (contentW + 2 * marginX) * scale, height: (contentH + 2 * marginY) * scale,
          maxWidth: "none", touchAction: "none",
        }}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        data-testid={testid}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          style={{
            position: "absolute", left: px(rect.left, marginX), top: px(rect.top, marginY),
            width: rect.width * scale, height: rect.height * scale,
            maxWidth: "none", opacity: 0.35, pointerEvents: "none",
          }}
        />
        {viewports.map((v) => (
          <div
            key={v.key}
            style={{
              position: "absolute", left: px(v.left, marginX), top: px(v.top, marginY),
              width: v.width * scale, height: v.height * scale, overflow: "hidden",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.45)", pointerEvents: "none",
            }}
          >
            <div style={{ width: v.width, height: v.height, transform: `scale(${scale})`, transformOrigin: "top left" }}>
              {v.node}
            </div>
          </div>
        ))}
        <div
          role="presentation"
          data-testid={`${testid}-box`}
          onPointerDown={down("move")}
          style={{
            position: "absolute", left: px(rect.left, marginX), top: px(rect.top, marginY),
            width: rect.width * scale, height: rect.height * scale,
            outline: `2px solid ${LIME}`, boxShadow: "0 0 0 3px rgba(0,0,0,0.55)",
            cursor: "move", touchAction: "none",
          }}
        >
          {CORNERS.map((c) => (
            <span
              key={c}
              data-testid={`${testid}-corner-${c}`}
              onPointerDown={down(c)}
              style={{
                position: "absolute", width: 16, height: 16, backgroundColor: "#fff",
                border: "2px solid #111", borderRadius: 3,
                [c.includes("n") ? "top" : "bottom"]: -8,
                [c.includes("w") ? "left" : "right"]: -8,
                cursor: c === "nw" || c === "se" ? "nwse-resize" : "nesw-resize",
                touchAction: "none",
              }}
            />
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2 text-[11px] text-[var(--ink-muted)]">{tr("Size")}<input
          type="range"
          min={MIN_ZOOM}
          max={Math.max(4, Math.ceil(zoom))}
          step={0.01}
          value={Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM)}
          onChange={(e) => setZoom(parseFloat(e.target.value))}
          className="flex-1"
          data-testid={`${testid}-size`}
        />
        <span className="w-10 text-right">{Math.round(zoom * 100)}%</span>
      </label>
    </div>
  );
}
